import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { PinkSwitch } from './ui/switch';
import { Mail, Bell, Newspaper, Users, Sparkles, Tv } from 'lucide-react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailPreferencesModal = ({ isOpen, onClose, userName }) => {
  const [preferences, setPreferences] = useState({
    feature_announcements: true,
    weekly_digest: true,
    social_notifications: true,
    blog_updates: true,
    grazing_recommendations: true
  });
  const [saving, setSaving] = useState(false);

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/email/preferences/set-initial`, preferences, {
        withCredentials: true
      });
      toast.success('Email preferences saved!');
      onClose();
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleOptOutAll = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/email/unsubscribe-all`, {}, {
        withCredentials: true
      });
      toast.success('Opted out of all emails');
      onClose();
    } catch (error) {
      console.error('Error opting out:', error);
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const preferenceItems = [
    {
      key: 'feature_announcements',
      icon: Sparkles,
      title: 'Feature Announcements',
      description: 'Get notified about new features and updates'
    },
    {
      key: 'weekly_digest',
      icon: Bell,
      title: 'Weekly Episode Digest',
      description: 'Monday email with your upcoming shows for the week'
    },
    {
      key: 'social_notifications',
      icon: Users,
      title: 'Social Activity',
      description: 'Comments on your reviews and new followers'
    },
    {
      key: 'grazing_recommendations',
      icon: Tv,
      title: 'Monthly Recommendations',
      description: 'AI-powered show suggestions based on your taste'
    },
    {
      key: 'blog_updates',
      icon: Newspaper,
      title: 'Blog Updates',
      description: 'New blog posts and TV news'
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="email-prefs-modal" data-testid="email-preferences-modal">
        <DialogHeader>
          <DialogTitle className="modal-title">
            <Mail className="title-icon" />
            Stay in the Loop
          </DialogTitle>
          <DialogDescription className="modal-description">
            Welcome{userName ? `, ${userName}` : ''}! Choose what emails you'd like to receive from Baad.tv. 
            You can change these anytime in Settings.
          </DialogDescription>
        </DialogHeader>

        <div className="preferences-list">
          {preferenceItems.map((item) => (
            <div key={item.key} className="preference-item" data-testid={`pref-${item.key}`}>
              <div className="preference-icon">
                <item.icon size={20} />
              </div>
              <div className="preference-info">
                <div className="preference-title">{item.title}</div>
                <div className="preference-description">{item.description}</div>
              </div>
              <PinkSwitch
                checked={preferences[item.key]}
                onCheckedChange={() => handleToggle(item.key)}
                data-testid={`switch-${item.key}`}
              />
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="save-btn"
            data-testid="save-preferences"
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
          <button 
            onClick={handleOptOutAll}
            disabled={saving}
            className="opt-out-link"
            data-testid="opt-out-all"
          >
            No thanks, opt out of all emails
          </button>
        </div>

        <style jsx>{`
          :global(.email-prefs-modal) {
            background: #1a1a1a !important;
            border: 1px solid #333 !important;
            max-width: 480px !important;
          }

          .modal-title {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            color: #ffffff;
            font-size: 1.5rem;
          }

          :global(.title-icon) {
            color: #E62F88;
          }

          .modal-description {
            color: #b0b0b0 !important;
            margin-top: 0.5rem;
          }

          .preferences-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin: 1.5rem 0;
          }

          .preference-item {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1rem;
            background: #2a2a2a;
            border-radius: 8px;
            border: 1px solid #333;
          }

          .preference-icon {
            color: #E62F88;
            flex-shrink: 0;
          }

          .preference-info {
            flex: 1;
          }

          .preference-title {
            color: #ffffff;
            font-weight: 500;
            margin-bottom: 0.25rem;
          }

          .preference-description {
            color: #888;
            font-size: 0.85rem;
          }

          .modal-actions {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            align-items: center;
          }

          .save-btn {
            width: 100%;
            background: #E62F88 !important;
            color: #000 !important;
            font-weight: 600;
          }

          .opt-out-link {
            background: none;
            border: none;
            color: #666;
            font-size: 0.85rem;
            cursor: pointer;
            padding: 0.5rem;
          }

          .opt-out-link:hover {
            color: #b0b0b0;
            text-decoration: underline;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreferencesModal;
